import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { HttpError } from '@/api/http/errors'
import { queryKeys } from '@/api/query-keys'
import { dictionaryService } from '@/api/services/dictionary.service'
import type { DictionaryResponse } from '@/api/types/dictionary'
import { logger } from '@/utils/logger'

const CONFLICT = 409

/**
 * Sends custom-word edits to the backend. Any read already running is cancelled and the cache is
 * updated first, so the screen answers at once and a stale read cannot overwrite the edit. When
 * every write has finished the dictionary is read again: after a failure that undoes the
 * optimistic edit (and `onError` tells the caller, so it can tell the user), and after a success it
 * covers a read that another editor's change event started mid-save. A 409 on add means the word
 * is already there, which is the state we wanted. Clearing the whole list sends a single
 * `POST /dictionary/clear`.
 */
export function useDictionaryWordSync(onError?: (error: unknown) => void) {
  const queryClient = useQueryClient()

  return useCallback(
    (before: string[], after: string[]) => {
      const added = after.filter((word) => !before.includes(word))
      const removed = before.filter((word) => !after.includes(word))

      if (added.length === 0 && removed.length === 0) return

      // A read that is already running was sent before this edit, so it would come back without
      // the edit and overwrite it. Cancel it before the optimistic update.
      void queryClient.cancelQueries({ queryKey: queryKeys.dictionary.all })

      queryClient.setQueryData<DictionaryResponse>(queryKeys.dictionary.all, (current) =>
        current ? { ...current, customWords: after } : current
      )

      // Emptying a list of two or more words in one edit is Clear All (a single remove takes out
      // one word), so it goes as one request instead of one remove per word.
      const isClearAll = after.length === 0 && removed.length > 1

      const writes = isClearAll
        ? [dictionaryService.clearWords()]
        : [
            ...added.map((word) =>
              dictionaryService.addWord(word).catch((error: unknown) => {
                if (error instanceof HttpError && error.status === CONFLICT) return

                throw error
              })
            ),
            ...removed.map((word) => dictionaryService.removeWord(word)),
          ]

      // Read the dictionary again once every write has finished, whether they worked or not. After
      // a failure that undoes the optimistic edit. After a success it replaces any read that
      // another editor's change event started while a write was still in flight, which would have
      // left this edit out.
      void Promise.all(writes)
        .catch((error: unknown) => {
          logger.error('Dictionary update failed', error)
          onError?.(error)
        })
        .finally(() => {
          void queryClient.invalidateQueries({ queryKey: queryKeys.dictionary.all })
        })
    },
    [queryClient, onError]
  )
}
